import { z } from 'zod';

export const MediaFileStatusSchema = z.enum(['pending', 'ready']);

export type MediaFileStatus = z.infer<typeof MediaFileStatusSchema>;

export const MediaFileSchema = z.object({
  id: z.uuid(),
  // Free-form consumer label describing the file (e.g. "avatar", "object-photo").
  // The media module stores it but attaches no policy to it — the consumer owns
  // any meaning, allow-list, and limit.
  description: z.string(),
  status: MediaFileStatusSchema,
  contentType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  originalFilename: z.string().nullable(),
  // Hex SHA-256 over the stored bytes, computed at confirm. Lets a consumer
  // dedupe by identical content across its own records (IMM-447.3's property
  // hand-off) without re-reading the object. Null for files confirmed before
  // the hash existed, and for a server-side copy whose source carried none.
  contentHash: z.string().nullable(),
  createdAt: z.iso.datetime(),
});

export type MediaFile = z.infer<typeof MediaFileSchema>;

const WorkspaceMediaPathParamsSchema = z.object({ workspaceId: z.uuid() });

export const CreateMediaUploadPathParamsSchema = WorkspaceMediaPathParamsSchema;

// The caller supplies the constraints (allow-list + cap) the media module
// enforces — the module holds no per-use-case policy of its own.
export const CreateMediaUploadBodySchema = z
  .object({
    description: z.string().trim().min(1).max(64),
    contentType: z.string().min(1).max(255),
    sizeBytes: z.number().int().positive(),
    allowedContentTypes: z.array(z.string().min(1).max(255)).min(1),
    maxSizeBytes: z.number().int().positive(),
    originalFilename: z.string().trim().min(1).max(255).nullish(),
  })
  .strict();

export type CreateMediaUploadBody = z.infer<typeof CreateMediaUploadBodySchema>;

export const CreateMediaUploadResponseSchema = z.object({
  fileId: z.uuid(),
  upload: z.object({
    url: z.url(),
    fields: z.record(z.string(), z.string()),
  }),
  expiresInSeconds: z.number().int().positive(),
});

export type CreateMediaUploadResponse = z.infer<typeof CreateMediaUploadResponseSchema>;

export const ConfirmMediaUploadPathParamsSchema = z.object({ workspaceId: z.uuid(), id: z.uuid() });

export const ConfirmMediaUploadResponseSchema = MediaFileSchema;

export type ConfirmMediaUploadResponse = z.infer<typeof ConfirmMediaUploadResponseSchema>;

// A short-lived presigned GET URL plus the metadata a consumer needs to render
// or download the file. The URL is minted per request and never stored.
export const MediaFileReadSchema = z.object({
  url: z.url(),
  expiresInSeconds: z.number().int().positive(),
  file: MediaFileSchema,
});

export type MediaFileRead = z.infer<typeof MediaFileReadSchema>;

export const ReadMediaFilePathParamsSchema = z.object({ workspaceId: z.uuid(), id: z.uuid() });

export const ReadMediaFileResponseSchema = MediaFileReadSchema;

export type ReadMediaFileResponse = z.infer<typeof ReadMediaFileResponseSchema>;

export const GetMediaFileMetadataPathParamsSchema = ReadMediaFilePathParamsSchema;

export const GetMediaFileMetadataResponseSchema = MediaFileSchema;

export type GetMediaFileMetadataResponse = z.infer<typeof GetMediaFileMetadataResponseSchema>;

// `read-many` is bounded so presign work per call stays bounded; consumers
// paginate beyond this.
export const MEDIA_READ_MANY_MAX = 100;

export const ReadManyMediaFilesPathParamsSchema = WorkspaceMediaPathParamsSchema;

export const ReadManyMediaFilesBodySchema = z
  .object({
    ids: z.array(z.uuid()).min(1).max(MEDIA_READ_MANY_MAX),
  })
  .strict();

export type ReadManyMediaFilesBody = z.infer<typeof ReadManyMediaFilesBodySchema>;

// Each requested id resolves to one entry; ids outside the workspace or not yet
// `ready` are silently omitted, so the result may be shorter than the request.
export const ReadManyMediaFilesResponseSchema = z.object({
  files: z.array(MediaFileReadSchema),
});

export type ReadManyMediaFilesResponse = z.infer<typeof ReadManyMediaFilesResponseSchema>;

export const DeleteMediaFilePathParamsSchema = ReadMediaFilePathParamsSchema;

export const DeleteMediaFileResponseSchema = z.object({ id: z.uuid() });

export type DeleteMediaFileResponse = z.infer<typeof DeleteMediaFileResponseSchema>;
